import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import appliedAiImage from '@/assets/images/applied-ai.avif';

const capabilities = [
  {
    icon: 'ri-chat-ai-line',
    title: 'Chatbots & Assistentes com LLM',
    desc: 'Assistentes virtuais treinados com o contexto do seu negócio, capazes de atender clientes, responder perguntas e executar tarefas complexas automaticamente.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Visão Computacional',
    desc: 'Sistemas que analisam imagens e vídeos em tempo real: inspeção de qualidade, reconhecimento de padrões, contagem de itens e muito mais.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Análise Preditiva',
    desc: 'Modelos de machine learning que antecipam comportamentos e tendências: previsão de vendas, churn, manutenção preditiva e demanda.',
  },
  {
    icon: 'ri-translate-2',
    title: 'Processamento de Linguagem Natural',
    desc: 'Extração de informações de textos não estruturados, classificação de documentos, análise de sentimentos e sumarização automática.',
  },
  {
    icon: 'ri-shield-flash-line',
    title: 'Detecção de Anomalias & Fraudes',
    desc: 'Algoritmos que identificam padrões incomuns em transações, operações e dados para proteger seu negócio em tempo real.',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Automação Cognitiva',
    desc: 'Processos que envolvem julgamento e tomada de decisão automatizados com IA, indo além do RPA tradicional.',
  },
];

const techStack = [
  'Python', 'TensorFlow', 'PyTorch', 'LLMs', 'LangChain',
  'Hugging Face', 'Flowise', 'Make', 'n8n', 'Azure ML'
];

export default function AISection() {
  const { ref, visible } = useScrollAnimation();
  const { ref: ref2, visible: visible2 } = useScrollAnimation();

  return (
    <section id="ai" className="py-20 md:py-28 relative overflow-hidden bg-zinc-950">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={appliedAiImage}
          alt="AI Background"
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/60 to-zinc-950/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div ref={ref} className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-emerald-400 font-semibold text-sm font-inter tracking-widest uppercase mb-3">Nosso diferencial</span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-white mb-4">Inteligência Artificial</h2>
          <p className="font-inter text-zinc-400 max-w-xl mx-auto text-base">
            Aplicamos as mais modernas tecnologias de IA para resolver problemas reais do negócio — com foco em resultado e sem complicações desnecessárias.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        {/* Capabilities grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {capabilities.map((c, i) => (
            <div
              key={c.title}
              className={`group rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-emerald-500/50 hover:bg-white/10 transition-all duration-500 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <i className={`${c.icon} text-emerald-400 text-xl`} />
              </div>
              <h4 className="font-sora font-semibold text-white text-sm mb-2">{c.title}</h4>
              <p className="font-inter text-zinc-400 text-sm leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        {/* Tech stack */}
        <div ref={ref2} className={`transition-all duration-700 ${visible2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-center font-sora font-semibold text-zinc-400 text-sm uppercase tracking-widest mb-6">
            Algumas das tecnologias que dominamos
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {techStack.map((t) => (
              <span
                key={t}
                className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-zinc-300 text-sm font-inter hover:border-emerald-500/50 hover:text-emerald-400 transition-colors cursor-default"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-14 transition-all duration-700 delay-300 ${visible2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="font-inter text-zinc-400 mb-6 text-base">
            Pronto para levar IA para dentro da sua operação?
          </p>
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-semibold font-sora text-base transition-all cursor-pointer whitespace-nowrap"
          >
            Vamos Conversar
          </button>
        </div>
      </div>
    </section>
  );
}
